'use client'

import GraphicChatInput from '@/components/Graphic/GraphicChatInput'
import GraphicChatMessageItemWidget from '@/components/Graphic/GraphicChatMessageItemWidget'
import { uploadFile } from '@/lib/api'
import { useGraphic } from '@/lib/features/graphic/GraphicContext'
import { useGraphicPreview } from '@/lib/features/graphic/GraphicPreviewContext'
import { ChatItemV2 } from '@/lib/type'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { GraphicSelection } from 'sprites-types/api/v2/graphics'
import { v4 as uuidv4 } from 'uuid'
import { getSelectionForNode } from './utils'

// This is mostly used for testing, and is not used in the app.
const defaultCreateFromPrompt = `
`

export default function GraphicInspector() {
  const { graphic, isLoading, createFromFile, createFromPrompt, sendMessage, getMessages } =
    useGraphic()
  const { selectedNodes } = useGraphicPreview()

  const [htmlFileUrl, setHtmlFileUrl] = useState<string | undefined>(undefined)
  const [htmlFileName, setHtmlFileName] = useState<string | undefined>(undefined)
  const [isUploading, setIsUploading] = useState(false)

  const [currentlyTypingMessageId, setCurrentlyTypingMessageId] = useState<string | null>(null)

  const messagesContainerRef = useRef<HTMLDivElement>(null)

  const messages = getMessages()

  // Convert GraphicInspector messages to ChatItemV2 format
  const convertToChatMessages = (messages: any[]): ChatItemV2[] => {
    return messages.map((message) => {
      const baseMessage = {
        id: message.id,
        type: 'message' as const,
        timestamp: new Date().toISOString(),
      }

      switch (message.type) {
        case 'update_graphic':
          return {
            ...baseMessage,
            role: 'user' as const,
            content: message.message,
          }
        case 'system':
          return {
            ...baseMessage,
            role: 'system' as const,
            message: message.message,
          }
        case 'error':
          return {
            ...baseMessage,
            role: 'assistant' as const,
            content: `<div style="font-style: italic">${message.message}</div>`,
          }
        default:
          return {
            ...baseMessage,
            role: 'system' as const,
            message: message.message || 'Unknown message type',
          }
      }
    })
  }

  const chatMessages = convertToChatMessages(messages)

  // Find the last assistant message that hasn't completed typing
  const getActiveTypewriterMessageId = (): string | null => {
    for (let i = chatMessages.length - 1; i >= 0; i--) {
      const message = chatMessages[i]
      if (message.role === 'assistant') {
        return message.id
      }
    }
    return null
  }

  // Update currently typing message when messages change
  useEffect(() => {
    const activeMessageId = getActiveTypewriterMessageId()
    if (activeMessageId && activeMessageId !== currentlyTypingMessageId) {
      setCurrentlyTypingMessageId(activeMessageId)
    }
  }, [chatMessages])

  // Scroll to bottom function
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth',
      })
    }
  }

  // Auto-scroll when messages change
  useEffect(() => {
    scrollToBottom()
  }, [chatMessages])

  const handleSubmit = async (text: string, _options: { files?: File[] }) => {
    if (!graphic || !text.trim()) {
      return
    }

    let selection: GraphicSelection | null = null
    if (selectedNodes.length > 0) {
      const node = selectedNodes[0] // Take only the first selected node
      selection = getSelectionForNode(node)
    }

    await sendMessage({
      id: uuidv4(),
      type: 'update_graphic',
      message: text.trim(),
      selection: selection,
    })

    setTimeout(scrollToBottom, 10)
  }

  const handleCreateFromFile = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!htmlFileUrl) {
      return
    }

    await createFromFile(htmlFileUrl)
    setHtmlFileUrl(undefined) // Clear the file after successful creation
    setHtmlFileName(undefined)
  }

  const handleCreateFromPrompt = async (text: string, _options: { files?: File[] }) => {
    if (!text.trim()) {
      return
    }

    const result = await createFromPrompt(text.trim())
    if (!result.success) {
      toast.error(result.error || 'Failed to create graphic from prompt')
      return
    }

    // Scroll to bottom after creating from prompt
    setTimeout(scrollToBottom, 100)
  }

  const handleFileUpdate = (fileUrl: string, fileName: string) => {
    setHtmlFileUrl(fileUrl || undefined)
    setHtmlFileName(fileName || undefined)
  }

  // Updated file upload handler that uses the API function
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setIsUploading(true)

    const result = await uploadFile(file)

    if (!result.success) {
      toast.error(result.error || 'File upload failed')
      setIsUploading(false)
      return
    }

    if (result.url) {
      handleFileUpdate(result.url, file.name)
      console.log('HTML file uploaded successfully:', result.url)
    } else {
      toast.error('File upload failed - no URL returned')
    }

    setIsUploading(false)
  }

  const getSelectedNodeDisplayContent = (): string => {
    if (selectedNodes.length === 0) return ''

    const selectedNode = selectedNodes[0]
    const textContent = selectedNode?.textContent?.trim() || ''

    let displayContent = ''
    if (textContent.length > 0) {
      displayContent = textContent.length > 15 ? textContent.substring(0, 15) + '...' : textContent
    } else {
      // If no text content, try to use the node's id
      const nodeId = (selectedNode as Element)?.id
      if (nodeId) {
        displayContent = nodeId.length > 15 ? nodeId.substring(0, 15) + '...' : nodeId
      }
    }

    return displayContent ? ` (${displayContent})` : ''
  }

  return (
    <div className="flex h-full flex-col rounded-3xl bg-white">
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* File Upload Form - Show only when no graphic exists */}
        {!graphic && (
          <div className="space-y-4 p-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Create Graphics from HTML File
              </label>
              <input
                type="file"
                accept=".html,.htm"
                onChange={handleFileSelect}
                disabled={isUploading || isLoading}
                className="mb-3 block w-full text-sm text-gray-900 file:mr-4 file:rounded-md file:border-0 file:bg-gray-100 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-gray-700 hover:file:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
              />
              {isUploading && <p className="mt-1 text-xs text-blue-600">Uploading file...</p>}
              {htmlFileName && !isUploading && (
                <p className="mt-1 text-xs text-gray-600">Uploaded file: {htmlFileName}</p>
              )}
            </div>

            {htmlFileUrl && !isUploading && (
              <form onSubmit={handleCreateFromFile}>
                <button
                  type="submit"
                  disabled={isLoading || !htmlFileUrl}
                  className="w-full rounded-md bg-green-600 px-4 py-2 text-white transition-colors hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isLoading ? 'Creating...' : 'Create Graphics'}
                </button>
              </form>
            )}
          </div>
        )}

        {/* Existing Update Form content - Show only when graphics exist */}
        {graphic && (
          <>
            {/* Messages Display using ChatMessageItemWidget */}
            {chatMessages.length > 0 && (
              <div ref={messagesContainerRef} className="flex-1 overflow-y-auto px-4 pt-2">
                {chatMessages.map((chatMessage, _index) => (
                  <GraphicChatMessageItemWidget key={chatMessage.id} chatMessage={chatMessage} />
                ))}
              </div>
            )}
          </>
        )}

        {!graphic && (
          <div className="p-4">
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Create Graphics from Prompt
            </label>
            <GraphicChatInput
              placeholder="Enter a prompt to create new graphics..."
              defaultValue={defaultCreateFromPrompt}
              onSendMessage={handleCreateFromPrompt}
              disabled={isLoading}
              enableFileUpload={false}
              enableReportButton={false}
              className="w-full"
            />
          </div>
        )}

        {!graphic && !htmlFileUrl && !isUploading && (
          <p className="p-4 text-sm text-gray-500">
            No graphics loaded. Upload an HTML file or create from prompt above to get started.
          </p>
        )}
      </div>

      {/* Chat widget positioned at the bottom */}
      <div className="border-t border-gray-200 p-4">
        {graphic && (
          <GraphicChatInput
            onSendMessage={handleSubmit}
            disabled={isLoading}
            isLoading={isLoading}
            contextComponent={
              selectedNodes.length > 0 ? (
                <div>
                  <span className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-800">
                    Selected{getSelectedNodeDisplayContent()}
                  </span>
                </div>
              ) : undefined
            }
            placeholder="Ask Sprites to update the graphics..."
            enableFileUpload={false}
            enableReportButton={false}
          />
        )}
      </div>
    </div>
  )
}
